import { Component, DestroyRef, effect, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ModalComponent,
	FormGroupComponent,
	SelectComponent,
	AlertComponent,
	ISelect,
	TileComponent,
	QrComponent,
	InfoGroupComponent,
	CopyTextComponent,
	ButtonComponent,
	InputComponent,
	LinkComponent,
	IconComponent,
	StepperMicroComponent,
	IStepProgress,
	EmptyStateComponent,
	DatepickerComponent,
} from '@tecalisdev/infinity';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { ibanValidator } from '../../../shared/validators/iban.validator';
import { nifValidator } from '../../../shared/validators/nif.validator';
import { HttpErrorResponse } from '@angular/common/http';
import {
	AuthChannel,
	IAuthTfaPayload,
	IWorkflowAuthMatchDataPayload,
	IWorkflowAuthResponse,
	IWorkflowDetailResponse,
	IWorkflowsListBody,
	IWorkflowsListResponse,
} from '../../../workflows/models/workflows.models';
import { WorkflowsService } from '../../../workflows/services/workflows.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { ProcessService } from '../../services/process.service';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { MenuItemAction } from '../../../shared/models/master.models';
import { CONNECTION_TYPE, IHomeOperation, IOperation, OPERATION_TYPE } from '../../../operations/models/operations.models';
import { MasterService } from '../../../shared/services/master.service';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import {
	AuthMatchDataControlName,
	buildAuthMatchIdentityDocumentTypeOptions,
	resolveFormAuthMatchDataControls,
} from '../../utils/form-auth-match-data.util';
import { PhoneInputComponent } from '../../../shared/components/phone-input/phone-input.component';
import { requiredPhoneE164Validator } from '../../../shared/validators/phone-e164.validator';
import { catchError, EMPTY, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
	selector: 'app-modal-create-process',
	imports: [
		ModalComponent,
		TranslatePipe,
		FormGroupComponent,
		SelectComponent,
		ReactiveFormsModule,
		AlertComponent,
		TileComponent,
		QrComponent,
		InfoGroupComponent,
		CopyTextComponent,
		ButtonComponent,
		InputComponent,
		PhoneInputComponent,
		LinkComponent,
		IconComponent,
		StepperMicroComponent,
		EmptyStateComponent,
		DatepickerComponent,
	],
	templateUrl: './modal-create-process.component.html',
	styleUrl: './modal-create-process.component.scss',
})
export class ModalCreateProcessComponent implements OnInit, OnDestroy {
	@Input() workflow?: ISelect;
	@Input() workflowResponse?: IWorkflowDetailResponse;
	@Output() closeEvent = new EventEmitter<boolean>();

	public reactiveFormService = inject(ReactiveFormsService);
	public masterService = inject(MasterService);
	public rolePermissions = inject(RolePermissionsService);
	private trans = inject(TranslateService);
	private workflowsService = inject(WorkflowsService);
	private loading = inject(LoadingService);
	private processService = inject(ProcessService);
	private modalConfirmationService = inject(ModalConfirmationService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly countryProvinceDestroy$ = new Subject<void>();

	public form: FormGroup = new FormGroup({
		company: new FormControl(''),
		workflow: new FormControl('', Validators.required),
		expiration: new FormControl(120, Validators.required),
		phone: new FormControl(''),
	});
	public formConfig: FormGroup = new FormGroup({});
	public expirationOptions: ISelect[] = this.masterService.expirationOptions;
	public steps: IStepProgress[] = [
		{ id: 'config', label: this.trans.instant('PROCESSES.CREATE_MODAL.STEP_CONF'), selected: true },
		{ id: 'links', label: this.trans.instant('PROCESSES.CREATE_MODAL.STEP_LINKS'), selected: false },
	];
	public formEmail: FormControl = new FormControl('', [Validators.required, Validators.email]);
	public formPhoneShare: FormControl = new FormControl('', [Validators.required, requiredPhoneE164Validator()]);
	public formPhoneWhatsapp: FormControl = new FormControl('', [Validators.required, requiredPhoneE164Validator()]);
	public link?: string = '';
	public processUuid?: string;
	public alertError: string;
	public workflows: ISelect[] = [];
	public companies: ISelect[] = this.masterService.companies;
	public loadingModal: string = '';
	public showModal: boolean = true;
	public showConfiguredForm: boolean = false;
	public showStepConfig: boolean = false;
	public countrySelectOptions: ISelect[] = [];
	public provinceSelectOptions: ISelect[] = [];
	private authMatchDocumentTypeSelectOptions: ISelect[] | null = null;
	public readonly smsResendCount = signal(0);
	public readonly smsResendLimit = 3;
	private readonly secondFactorStoragePrefix = 'nif204_second_factor_';

	constructor() {
		this.form.valueChanges.subscribe((value: { workflow: ISelect; expiration: ISelect }) => {
			this.syncPhoneValidation();
			this.persistSecondFactorState();
			if (value.workflow && value.expiration) {
				if (this.workflowResponse && this.workflowResponse.uuid === value.workflow.id.toString()) {
					if (!this.showStepConfig) this.generateUrl();
				} else {
					this.getWorkflowDetail(value.workflow.id.toString(), !this.showStepConfig);
				}
			}
		});
		effect(() => {
			this.masterService.master();
			this.refreshCountrySelectOptions();
		});
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			// Acción de cambiar estado
			if (modalResponse?.id?.menuAction === MenuItemAction.DUPLICATE) {
				if (modalResponse?.action === 'buttonSecondary') {
					this.showModal = true;
					this.generateUrl();
				} else {
					this.closeEvent.emit(false);
				}
			} else if (modalResponse?.id?.menuAction === MenuItemAction.CANCEL) {
				if (modalResponse?.action === 'buttonPrimary') {
					this.showModal = true;
				} else {
					this.closeEvent.emit(false);
				}
			}
		});
	}

	ngOnInit(): void {
		this.refreshCountrySelectOptions();
		// Mostrar el step de configuración si tiene 2FA
		this.showStepConfig =
			this.workflowResponse?.config?.metadata?.two_factor_channel === 'sms' ||
			this.workflowResponse?.config?.metadata?.two_factor_channel === 'whatsapp';

		// Desde detalle de un workflow
		if (this.workflow && this.workflowResponse) {
			if (this.hasOperationConfigured(this.workflowResponse)) {
				this.completeFormConfig();
				this.showStepConfig = true;
				this.showConfiguredForm = true;
				this.form.get('workflow').setValue(this.workflow, { emitEvent: false });
			} else {
				this.form.get('workflow').setValue(this.workflow);
			}
		}
		// Desde listado de workflows
		if (this.workflow && !this.workflowResponse) {
			this.getWorkflowDetail(this.workflow.id.toString());
		}
		// Desde listado de procesos
		if (!this.workflow) {
			this.showStepConfig = true;
			this.getWorkflowList();
		}
		this.syncPhoneValidation();
	}

	ngOnDestroy(): void {
		this.countryProvinceDestroy$.next();
		this.countryProvinceDestroy$.complete();
	}

	public closeModal(type: string) {
		const actions = {
			buttonSecondary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					return;
				}
				if (this.hasOperationConfigured(this.workflowResponse) && this.formConfig.invalid) {
					this.formConfig.markAllAsTouched();
					return;
				}
				this.generateUrl();
			},
			buttonPrimary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					return;
				}
				if (this.hasOperationConfigured(this.workflowResponse) && this.formConfig.invalid) {
					this.formConfig.markAllAsTouched();
					return;
				}
				if (!this.hasValidHomeAndTermsFlow()) {
					this.alertError = this.trans.instant('PROCESSES.CREATE_MODAL.HOME_TERMS_REQUIRED');
					return;
				}
				this.alertError = '';
				this.clickStep(this.steps.find((s) => s.id === 'links')!);
				this.generateUrl();
			},
		};

		actions[type] ? actions[type]() : this.closeEvent.emit(false);
	}

	public generateUrl(
		workflow: ISelect = this.workflow ? this.workflow : this.form.get('workflow').value,
		advanceToLinksStep = false,
	): void {
		if (!this.workflow && this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		if (this.hasOperationConfigured(this.workflowResponse) && this.formConfig.invalid) {
			this.formConfig.markAllAsTouched();
			return;
		}
		if (!this.hasValidHomeAndTermsFlow()) {
			this.alertError = this.trans.instant('PROCESSES.CREATE_MODAL.HOME_TERMS_REQUIRED');
			return;
		}
		const matchData = this.hasOperationConfigured(this.workflowResponse) ? this.getMatchData() : undefined;
		if (this.hasOperationConfigured(this.workflowResponse) && !matchData) {
			this.formConfig.markAllAsTouched();
			return;
		}
		this.link = '';
		this.processUuid = undefined;
		this.formEmail.reset();
		this.formPhoneShare.reset(this.normalizedPhone() ?? '');
		this.formPhoneWhatsapp.reset(this.normalizedPhone() ?? '');
		this.alertError = '';
		this.loadingModal = this.trans.instant('PROCESSES.CREATE_MODAL.LOADING_GENERATING');
		this.workflowsService
			.createAuth({
				api_key: workflow.value.api_key,
				profile_uuid: workflow.id.toString(),
				short_url_pwcs: true,
				token_duration_minutes: this.form.get('expiration').value?.id ?? this.form.get('expiration').value,
				origin: 'admin',
				match_data: matchData,
				tfa: this.buildTfaPayload(),
			})
			.subscribe({
				next: (response: IWorkflowAuthResponse) => {
					this.link = response.pwcs_url;
					this.processUuid = response.process;
					this.persistSecondFactorState();
					if (advanceToLinksStep && this.link) {
						this.clickStep(this.steps.find((s) => s.id === 'links')!);
					}
				},
				error: (error: HttpErrorResponse) => {
					this.loadingModal = '';
					this.alertError = this.trans.instant('WORKFLOWS.LIST.GENERATE_AUTH_ERROR');
				},
				complete: () => {
					this.loadingModal = '';
				},
			});
	}

	public sendLinkToEmail() {
		if (this.formEmail.invalid) {
			this.formEmail.markAsTouched();
			return;
		}
		if (!this.processUuid) {
			this.alertError = this.trans.instant('PROCESSES.CREATE_MODAL.SEND_KO');
			return;
		}
		this.loadingModal = this.trans.instant('PROCESSES.CREATE_MODAL.LOADING_SENDING');
		this.processService
			.shareLink({
				process: this.processUuid,
				link: this.link,
				email: this.formEmail.value,
			})
			.subscribe({
				next: (response: any) => {
					this.showModal = false;
					this.modalConfirmationService.show({
						title: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_OK'),
						description: '',
						buttonPrimaryLabel: '',
						buttonSecondaryLabel: this.trans.instant('PROCESSES.CREATE_MODAL.GENERATE_OTHER_LINK'),
						buttonPrimaryStyle: true,
						iconStatus: 'success',
						icon: 'icon-check',
						id: { id: this.link, menuAction: MenuItemAction.DUPLICATE },
					});
				},
				error: (error: HttpErrorResponse) => {
					this.showModal = false;
					const apiMessage =
						typeof error.error?.message === 'string' && error.error.message !== 'Server Error'
							? error.error.message
							: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_KO_DESCRIPTION');
					this.modalConfirmationService.show({
						title: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_KO'),
						description: apiMessage,
						buttonPrimaryLabel: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_KO_BUTTON'),
						buttonSecondaryLabel: '',
						id: { id: this.link, menuAction: MenuItemAction.CANCEL },
					});
					this.loadingModal = '';
				},
				complete: () => {
					this.loadingModal = '';
				},
			});
	}

	public sendLinkToSms(): void {
		if (this.formPhoneShare.invalid) {
			this.formPhoneShare.markAsTouched();
			return;
		}
		if (!this.processUuid) {
			this.alertError = this.trans.instant('PROCESSES.CREATE_MODAL.SEND_SMS_KO');
			return;
		}
		this.loadingModal = this.trans.instant('PROCESSES.CREATE_MODAL.LOADING_SENDING');
		this.processService
			.shareLink({
				process: this.processUuid,
				link: this.link,
				phone: this.formPhoneShare.value,
			})
			.subscribe({
				next: () => {
					this.showModal = false;
					this.modalConfirmationService.show({
						title: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_SMS_OK'),
						description: '',
						buttonPrimaryLabel: '',
						buttonSecondaryLabel: this.trans.instant('PROCESSES.CREATE_MODAL.GENERATE_OTHER_LINK'),
						buttonPrimaryStyle: true,
						iconStatus: 'success',
						icon: 'icon-check',
						id: { id: this.link, menuAction: MenuItemAction.DUPLICATE },
					});
				},
				error: (error: HttpErrorResponse) => {
					this.showModal = false;
					const apiMessage =
						typeof error.error?.message === 'string' && error.error.message !== 'Server Error'
							? error.error.message
							: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_SMS_KO_DESCRIPTION');
					this.modalConfirmationService.show({
						title: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_SMS_KO'),
						description: apiMessage,
						buttonPrimaryLabel: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_SMS_KO_BUTTON'),
						buttonSecondaryLabel: '',
						id: { id: this.link, menuAction: MenuItemAction.CANCEL },
					});
					this.loadingModal = '';
				},
				complete: () => {
					this.loadingModal = '';
				},
			});
	}

	public sendLinkToWhatsapp(): void {
		if (this.formPhoneWhatsapp.invalid) {
			this.formPhoneWhatsapp.markAsTouched();
			return;
		}
		if (!this.processUuid) {
			this.alertError = this.trans.instant('PROCESSES.CREATE_MODAL.SEND_WHATSAPP_KO');
			return;
		}
		this.loadingModal = this.trans.instant('PROCESSES.CREATE_MODAL.LOADING_SENDING');
		this.processService
			.shareLink({
				process: this.processUuid,
				link: this.link,
				phone: this.formPhoneWhatsapp.value,
				channel: 'whatsapp',
			})
			.subscribe({
				next: () => {
					this.showModal = false;
					this.modalConfirmationService.show({
						title: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_WHATSAPP_OK'),
						description: '',
						buttonPrimaryLabel: '',
						buttonSecondaryLabel: this.trans.instant('PROCESSES.CREATE_MODAL.GENERATE_OTHER_LINK'),
						buttonPrimaryStyle: true,
						iconStatus: 'success',
						icon: 'icon-check',
						id: { id: this.link, menuAction: MenuItemAction.DUPLICATE },
					});
				},
				error: (error: HttpErrorResponse) => {
					this.showModal = false;
					const status = error.status;
					let description =
						typeof error.error?.message === 'string' && error.error.message !== 'Server Error'
							? error.error.message
							: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_WHATSAPP_KO_DESCRIPTION');
					if (status === 429) {
						description = this.trans.instant('PROCESSES.CREATE_MODAL.SEND_WHATSAPP_LIMIT');
					}
					this.modalConfirmationService.show({
						title: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_WHATSAPP_KO'),
						description,
						buttonPrimaryLabel: this.trans.instant('PROCESSES.CREATE_MODAL.SEND_WHATSAPP_KO_BUTTON'),
						buttonSecondaryLabel: '',
						id: { id: this.link, menuAction: MenuItemAction.CANCEL },
					});
					this.loadingModal = '';
				},
				complete: () => {
					this.loadingModal = '';
				},
			});
	}

	public getWorkflowList(companyUuid?: string): void {
		this.alertError = '';
		this.loading.show(this.trans.instant('LOADING.WORKFLOWS_LIST'));

		const body = {
			sort_by: 'updated_at',
			sort_order: 'desc',
			per_page: '100',
		};
		if (companyUuid) body['company_uuid'] = companyUuid;

		this.workflowsService.list(body).subscribe({
			next: (response: IWorkflowsListResponse) => {
				this.workflows = response.data.map((workflow: IWorkflowsListBody) => ({
					id: workflow.uuid,
					label: workflow.alias,
					value: workflow,
				}));
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public getWorkflowDetail(id: string, forceRequest: boolean = false): void {
		this.alertError = '';
		this.link = '';
		this.processUuid = undefined;
		this.loading.show(this.trans.instant('LOADING.GET_WORKFLOWS'));
		this.workflowsService.detail(id).subscribe({
			next: (response: IWorkflowDetailResponse) => {
				this.workflowResponse = response;
				this.restoreSecondFactorState(response.uuid);
				this.showConfiguredForm = this.hasOperationConfigured(this.workflowResponse);
				if (this.showConfiguredForm) {
					this.completeFormConfig();
				}
				this.showStepConfig =
					this.showConfiguredForm ||
					!this.workflow ||
					this.workflowResponse?.config?.metadata?.two_factor_channel === 'sms' ||
					this.workflowResponse?.config?.metadata?.two_factor_channel === 'whatsapp';
				if (!this.form.get('workflow').value) {
					this.form.get('workflow').setValue(this.workflow, { emitEvent: !this.showStepConfig });
				}
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public shouldShowPhoneField(): boolean {
		return this.isWorkflowTwoFactorEnabled();
	}

	private buildTfaPayload(): IAuthTfaPayload | undefined {
		const channel = this.resolveWorkflowConfiguredChannel();
		const phone = this.normalizedPhone();
		if (channel === 'none' || !phone) {
			return undefined;
		}
		return {
			phone,
			type: channel,
		};
	}

	private normalizedPhone(): string | undefined {
		const phone = String(this.form.get('phone')?.value ?? '').trim();
		return phone === '' ? undefined : phone;
	}

	private syncPhoneValidation(): void {
		const phoneControl = this.form.get('phone');
		if (!phoneControl) {
			return;
		}
		if (!this.isWorkflowTwoFactorEnabled()) {
			phoneControl.patchValue('', { emitEvent: false });
			phoneControl.setValidators([]);
			phoneControl.updateValueAndValidity({ emitEvent: false });
			return;
		}
		const validators = [Validators.required, requiredPhoneE164Validator()];
		phoneControl.setValidators(validators);
		phoneControl.updateValueAndValidity({ emitEvent: false });
	}

	private hasValidHomeAndTermsFlow(): boolean {
		if (!this.workflowResponse?.operations?.length) {
			return true;
		}
		const homeOperation = this.findHomeOperation(this.workflowResponse);
		if (!homeOperation) {
			return true;
		}
		const tecalisPolicy = homeOperation?.config?.tecalis_policy_check === true;
		const additionalTerms = Array.isArray(homeOperation?.config?.policy_checks)
			? homeOperation.config.policy_checks.length
			: 0;
		return tecalisPolicy || additionalTerms > 0;
	}

	private findHomeOperation(workflow: IWorkflowDetailResponse): IHomeOperation | undefined {
		return this.listWorkflowOperations(workflow.operations ?? []).find(
			(operation: IOperation) => operation.type === OPERATION_TYPE.HOME,
		) as IHomeOperation | undefined;
	}

	private listWorkflowOperations(operations: IOperation[]): IOperation[] {
		return operations.flatMap((operation: IOperation) => {
			if (operation.type === OPERATION_TYPE.CHOOSE) {
				return [operation, ...(operation.config?.operations ?? [])];
			}
			return [operation];
		});
	}

	private persistSecondFactorState(): void {
		const workflow = this.workflow ?? this.form.get('workflow')?.value;
		const workflowId = workflow?.id?.toString?.();
		if (!workflowId) {
			return;
		}
		const payload = {
			phone: this.normalizedPhone() ?? '',
			smsResendCount: this.smsResendCount(),
		};
		localStorage.setItem(this.secondFactorStoragePrefix + workflowId, JSON.stringify(payload));
	}

	private restoreSecondFactorState(workflowId: string): void {
		const raw = localStorage.getItem(this.secondFactorStoragePrefix + workflowId);
		if (!raw) {
			this.smsResendCount.set(0);
			this.syncPhoneValidation();
			return;
		}
		try {
			const parsed = JSON.parse(raw) as {
				phone?: string;
				smsResendCount?: number;
			};
			this.form.patchValue(
				{
					phone: parsed.phone ?? '',
				},
				{ emitEvent: false },
			);
			this.smsResendCount.set(Math.max(0, Math.min(this.smsResendLimit, Number(parsed.smsResendCount) || 0)));
			this.syncPhoneValidation();
		} catch {
			this.smsResendCount.set(0);
		}
	}

	private resolveWorkflowConfiguredChannel(): AuthChannel {
		const fromTfa = this.workflowResponse?.config?.tfa?.type;
		if (fromTfa === 'sms' || fromTfa === 'whatsapp') {
			return fromTfa;
		}
		const metadata = this.workflowResponse?.config?.metadata as Record<string, unknown> | undefined;
		const fromMetadata = metadata?.['two_factor_channel'];
		if (fromMetadata === 'sms' || fromMetadata === 'whatsapp') {
			return fromMetadata;
		}
		return 'none';
	}

	public isWorkflowTwoFactorEnabled(): boolean {
		return this.resolveWorkflowConfiguredChannel() !== 'none';
	}

	public downloadQr(): void {
		const svg = document.querySelector('qrcode svg') as SVGSVGElement;
		if (!svg) return;
		const serializer = new XMLSerializer();
		const svgString = serializer.serializeToString(svg);

		const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
		const url = URL.createObjectURL(blob);

		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) return;

			// Pintar la imagen en el canvas
			ctx.drawImage(img, 0, 0);

			// Convertir el canvas a PNG
			canvas.toBlob((blob) => {
				if (!blob) return;
				const a = document.createElement('a');
				a.href = URL.createObjectURL(blob);
				a.download = 'qr.png'; // o qr.jpg si quieres JPG
				a.click();

				URL.revokeObjectURL(a.href);
			}, 'image/png');

			// Liberar URL del SVG
			URL.revokeObjectURL(url);
		};

		img.src = url;
	}

	private completeFormConfig(): void {
		if (!this.workflowResponse) return;
		const w = this.workflowResponse;
		const configRequired = !this.hasChooseOperation(w);
		this.formConfig = new FormGroup({});
		if (this.showIban(w)) {
			const ibanValidators = configRequired ? [Validators.required, ibanValidator()] : [ibanValidator()];
			this.addMatchDataControl('iban', ibanValidators);
		}
		if (this.showNie(w)) {
			// Carta verde (`required_check_nie`): el identificador en autorización es opcional; la comprobación fina va en backend tras el OCR.
			this.addMatchDataControl('nie', [nifValidator()]);
		}
		if (this.showFullName(w)) {
			const nameValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('names', nameValidators);
			this.addMatchDataControl('surnames', nameValidators);
		}
		if (this.showCif(w)) {
			const cifValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('cif', cifValidators);
		}
		if (this.showIdentifier(w)) {
			const identifierValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('identifier', identifierValidators);
		}
		if (this.showCountry(w)) {
			const countryValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('country', countryValidators);
		}
		if (this.showBirthdate(w)) {
			const birthdateValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('birthdate', birthdateValidators);
		}
		if (this.showExpirationDate(w)) {
			const expirationValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('expiration_date', expirationValidators);
		}
		if (this.showSupportNumber(w)) {
			const supportValidators = configRequired ? [Validators.required] : [];
			this.addMatchDataControl('support_number', supportValidators);
		}
		this.addFormMatchDataControls(w, configRequired);
		this.setupCountryProvinceCascade();
	}

	/**
	 * Cascada país → provincia (NIF-520): select de provincias lazy; reset al cambiar país.
	 */
	private setupCountryProvinceCascade(): void {
		this.countryProvinceDestroy$.next();
		this.provinceSelectOptions = [];

		const countryControl = this.formConfig.get('country');
		const provinceControl = this.formConfig.get('province');
		if (!countryControl || !provinceControl) {
			return;
		}

		provinceControl.disable({ emitEvent: false });

		countryControl.valueChanges
			.pipe(
				startWith(countryControl.value),
				switchMap((raw) => {
					const countryCode = this.extractSelectControlId(raw);
					this.resetProvinceAfterCountryChange(provinceControl);
					this.provinceSelectOptions = [];
					if (!countryCode) {
						provinceControl.disable({ emitEvent: false });
						return EMPTY;
					}
					provinceControl.disable({ emitEvent: false });
					return this.masterService.getProvinces(countryCode).pipe(
						catchError(() => of({ data: [] })),
					);
				}),
				takeUntil(this.countryProvinceDestroy$),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((response) => {
				const rows = response?.data ?? [];
				this.provinceSelectOptions = rows.map((province) => ({
					id: province.name,
					label: province.name,
				}));
				if (this.provinceSelectOptions.length > 0) {
					provinceControl.enable({ emitEvent: false });
				} else {
					provinceControl.disable({ emitEvent: false });
				}
			});
	}

	private resetProvinceAfterCountryChange(provinceControl: AbstractControl): void {
		provinceControl.setValue('', { emitEvent: false });
		provinceControl.markAsPristine();
		provinceControl.markAsUntouched();
	}

	/** Valor de `tec-select` (ISelect) o string/ISO. */
	private extractSelectControlId(raw: unknown): string {
		if (raw && typeof raw === 'object' && 'id' in (raw as object)) {
			const id = (raw as { id?: unknown }).id;
			return id === null || id === undefined ? '' : String(id).trim().toUpperCase();
		}
		if (raw === null || raw === undefined) {
			return '';
		}
		return String(raw).trim().toUpperCase();
	}

	private addFormMatchDataControls(workflow: IWorkflowDetailResponse, configRequired: boolean): void {
		const defaultValidators = configRequired ? [Validators.required] : [];
		for (const controlName of resolveFormAuthMatchDataControls(workflow)) {
			const validators = controlName === 'address_door' ? [] : defaultValidators;
			this.addMatchDataControl(controlName, validators);
		}
	}

	private addMatchDataControl(controlName: AuthMatchDataControlName, validators = []): void {
		if (this.formConfig.contains(controlName)) {
			return;
		}
		this.formConfig.addControl(controlName, new FormControl('', validators));
	}

	get formConfigControls(): { name: string; type: 'input' | 'date' | 'select' }[] {
		return Object.keys(this.formConfig.controls).map((name) => ({
			name,
			type: this.getControlType(name),
		}));
	}

	public getControlType(controlName: string): 'input' | 'date' | 'select' {
		const dateControls = ['birthdate', 'expiration_date'];
		if (dateControls.includes(controlName)) return 'date';
		if (controlName === 'country' || controlName === 'id_document_type' || controlName === 'province') {
			return 'select';
		}
		return 'input';
	}

	public getSelectOptions(controlName: string): ISelect[] {
		if (controlName === 'id_document_type') {
			return this.getAuthMatchDocumentTypeSelectOptions();
		}
		if (controlName === 'country') {
			return this.countrySelectOptions;
		}
		if (controlName === 'province') {
			return this.provinceSelectOptions;
		}
		return [];
	}

	private getAuthMatchDocumentTypeSelectOptions(): ISelect[] {
		if (!this.authMatchDocumentTypeSelectOptions) {
			this.authMatchDocumentTypeSelectOptions = buildAuthMatchIdentityDocumentTypeOptions((key) =>
				this.trans.instant(key),
			);
		}
		return this.authMatchDocumentTypeSelectOptions;
	}

	private refreshCountrySelectOptions(): void {
		const countries = this.masterService.countries;
		this.countrySelectOptions = countries ? [...countries] : [];
	}

	public selectControlSupportsSearch(controlName: string): boolean {
		return controlName === 'country' || controlName === 'province';
	}

	public getLabelControl(controlName: string): string {
		const labels: Record<string, string> = {
			iban: this.trans.instant('PROCESSES.CREATE_MODAL.IBAN'),
			identifier: this.trans.instant('PROCESSES.CREATE_MODAL.IDENTIFIER'),
			cif: this.trans.instant('PROCESSES.CREATE_MODAL.SCRIPTURE'),
			nie: this.trans.instant('PROCESSES.CREATE_MODAL.NIE'),
			names: this.trans.instant('PROCESSES.CREATE_MODAL.NAMES'),
			surnames: this.trans.instant('PROCESSES.CREATE_MODAL.SURNAMES'),
			country: this.trans.instant('PROCESSES.CREATE_MODAL.COUNTRY'),
			birthdate: this.trans.instant('PROCESSES.CREATE_MODAL.BIRTHDATE'),
			expiration_date: this.trans.instant('PROCESSES.CREATE_MODAL.EXPIRATION_DATE'),
			support_number: this.trans.instant('PROCESSES.CREATE_MODAL.SUPPORT_NUMBER'),
			id_document_type: this.trans.instant(
				'WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ID_DOCUMENT_TYPE',
			),
			address: this.trans.instant('PROCESSES.DETAIL.ADDRESS'),
			address_number: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_NUMBER'),
			address_floor: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_FLOOR'),
			address_door: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.PRESET_ADDRESS_DOOR'),
			postal_code: this.trans.instant('PROCESSES.DETAIL.AUTH_MATCH_FIELD.postal_code'),
			municipality: this.trans.instant('PROCESSES.DETAIL.CITY'),
			province: this.trans.instant('PROCESSES.DETAIL.PROVINCE'),
		};
		return labels[controlName] ?? controlName;
	}

	private hasOcrOperation(workflow: IWorkflowDetailResponse): boolean {
		return workflow?.operations?.some((op: IOperation) => op.type === OPERATION_TYPE.OCR) ?? false;
	}

	private hasChooseOperation(workflow: IWorkflowDetailResponse): boolean {
		return workflow?.operations?.some((op: IOperation) => op.type === OPERATION_TYPE.CHOOSE) ?? false;
	}

	private showIban(workflow: IWorkflowDetailResponse): boolean {
		return workflow?.operations?.some(
			(op: IOperation) =>
				(op.type === OPERATION_TYPE.CONNECTIONS && op.config.type === CONNECTION_TYPE.BANK_SCORING) ||
				(op.type === OPERATION_TYPE.CHOOSE &&
					op.config.operations.some(
						(o) => o.type === OPERATION_TYPE.CONNECTIONS && o.config.type === CONNECTION_TYPE.BANK_SCORING,
					)),
		);
	}

	private showNie(workflow: IWorkflowDetailResponse): boolean {
		const opRequiresGreenCard = (op: IOperation): boolean =>
			op.type === OPERATION_TYPE.OCR && op.config?.required_check_nie === true;
		return (
			workflow?.operations?.some(
				(op: IOperation) =>
					opRequiresGreenCard(op) ||
					(op.type === OPERATION_TYPE.CHOOSE &&
						op.config?.operations?.some((o: IOperation) => opRequiresGreenCard(o))),
			) ?? false
		);
	}

	private showCif(workflow: IWorkflowDetailResponse): boolean {
		return workflow?.operations?.some(
			(op: IOperation) =>
				(op.type === OPERATION_TYPE.CONNECTIONS && op.config.type === CONNECTION_TYPE.SCRIPTURE) ||
				(op.type === OPERATION_TYPE.CHOOSE &&
					op.config.operations.some(
						(o) => o.type === OPERATION_TYPE.CONNECTIONS && o.config.type === CONNECTION_TYPE.SCRIPTURE,
					)),
		);
	}

	private showWorklife(workflow: IWorkflowDetailResponse): boolean {
		return workflow?.operations?.some(
			(op: IOperation) =>
				(op.type === OPERATION_TYPE.CONNECTIONS && op.config.type === CONNECTION_TYPE.WORK_LIFE) ||
				(op.type === OPERATION_TYPE.CHOOSE &&
					op.config.operations.some(
						(o) => o.type === OPERATION_TYPE.CONNECTIONS && o.config.type === CONNECTION_TYPE.WORK_LIFE,
					)),
		);
	}

	private showIrpf(workflow: IWorkflowDetailResponse): boolean {
		return workflow?.operations?.some(
			(op: IOperation) =>
				(op.type === OPERATION_TYPE.CONNECTIONS && op.config.type === CONNECTION_TYPE.IRPF) ||
				(op.type === OPERATION_TYPE.CHOOSE &&
					op.config.operations.some(
						(o) => o.type === OPERATION_TYPE.CONNECTIONS && o.config.type === CONNECTION_TYPE.IRPF,
					)),
		);
	}

	private showFullName(workflow: IWorkflowDetailResponse): boolean {
		const hasOcr = this.hasOcrOperation(workflow);
		return (this.showIban(workflow) || this.showCif(workflow)) && !hasOcr;
	}

	private showIdentifier(workflow: IWorkflowDetailResponse): boolean {
		const hasOcr = this.hasOcrOperation(workflow);
		if (hasOcr) return false;
		return this.showCif(workflow) || this.showWorklife(workflow) || this.showIrpf(workflow);
	}

	private showCountry(workflow: IWorkflowDetailResponse): boolean {
		return this.showCif(workflow) && !this.hasOcrOperation(workflow);
	}

	private showBirthdate(workflow: IWorkflowDetailResponse): boolean {
		return this.showWorklife(workflow) && !this.hasOcrOperation(workflow);
	}

	private showExpirationDate(workflow: IWorkflowDetailResponse): boolean {
		return this.showIrpf(workflow) && !this.hasOcrOperation(workflow);
	}

	private showSupportNumber(workflow: IWorkflowDetailResponse): boolean {
		return this.showIrpf(workflow) && !this.hasOcrOperation(workflow);
	}

	private hasOperationConfigured(workflow: IWorkflowDetailResponse): boolean {
		return (
			this.showIban(workflow) ||
			this.showNie(workflow) ||
			this.showCif(workflow) ||
			this.showFullName(workflow) ||
			this.showIdentifier(workflow) ||
			this.showCountry(workflow) ||
			this.showBirthdate(workflow) ||
			this.showExpirationDate(workflow) ||
			this.showSupportNumber(workflow) ||
			resolveFormAuthMatchDataControls(workflow).length > 0
		);
	}

	private getMatchData(): IWorkflowAuthMatchDataPayload {
		if (!this.formConfig.valid) return undefined;
		const raw = this.formConfig.getRawValue() as Record<string, unknown>;
		const payload: Record<string, unknown> = { ...raw };
		const selectKeys = ['country', 'id_document_type', 'province'];
		for (const key of selectKeys) {
			if (payload[key] != null && typeof payload[key] === 'object' && 'id' in (payload[key] as object)) {
				payload[key] = (payload[key] as ISelect).id;
			}
		}
		// El datepicker devuelve Date; la API espera string (YYYY-MM-DD)
		const dateKeys = ['birthdate', 'expiration_date'];
		for (const key of dateKeys) {
			if (payload[key] instanceof Date) {
				const d = payload[key] as Date;
				payload[key] =
					`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
			}
		}
		if (typeof payload['nie'] === 'string') {
			const trimmed = (payload['nie'] as string).trim().toUpperCase();
			if (trimmed === '') {
				delete payload['nie'];
			} else {
				payload['nie'] = trimmed;
			}
		}
		return payload as unknown as IWorkflowAuthMatchDataPayload;
	}

	public clickStep(step: IStepProgress): void {
		if (step.id === 'links' && this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		this.steps.forEach((item) => {
			item.selected = false;
		});
		step.selected = true;
	}

	public updateOptionsByCompany(): void {
		const companyUuid = this.form.get('company').value?.id || this.form.get('company').value || null;
		this.form.get('workflow').setValue('');
		this.getWorkflowList(companyUuid);
	}
}
